/**
 * Utilities for working with user roles
 */

export interface RoleDisplay {
  label: string;
  color: string;
  badge: string;
}

/**
 * Get display information for a role
 */
export function getRoleDisplay(user: any): RoleDisplay {
  if (!user) {
    return {
      label: 'Utilizador',
      color: 'text-gray-600 dark:text-gray-400',
      badge: '👤',
    };
  }

  // Try to get role name from primaryRole first
  const roleName = user.primaryRole?.name || user.role || '';
  const roleSlug = (user.primaryRole?.slug || user.role || '').toLowerCase();

  // Map slugs to display info
  const roleMap: Record<string, RoleDisplay> = {
    'super-admin': {
      label: 'Super Admin',
      color: 'text-purple-600 dark:text-purple-400',
      badge: '👑',
    },
    'admin': {
      label: 'Administrador',
      color: 'text-red-600 dark:text-red-400',
      badge: '🛡️',
    },
    'administrator': {
      label: 'Administrador',
      color: 'text-red-600 dark:text-red-400',
      badge: '🛡️',
    },
    'project-manager': {
      label: 'Gestor de Projetos',
      color: 'text-blue-600 dark:text-blue-400',
      badge: '📊',
    },
    'manager': {
      label: 'Gestor',
      color: 'text-blue-600 dark:text-blue-400',
      badge: '📊',
    },
    'member': {
      label: 'Membro',
      color: 'text-green-600 dark:text-green-400',
      badge: '👤',
    },
    'viewer': {
      label: 'Visualizador',
      color: 'text-gray-600 dark:text-gray-400',
      badge: '👁️',
    },
  };

  // Return mapped display or use the role name directly
  const display = roleMap[roleSlug];

  if (display) {
    return display;
  }

  // If we have a custom role name, use it
  if (roleName) {
    return {
      label: roleName,
      color: 'text-gray-600 dark:text-gray-400',
      badge: '👤',
    };
  }

  // Default fallback
  return {
    label: 'Utilizador',
    color: 'text-gray-600 dark:text-gray-400',
    badge: '👤',
  };
}

/**
 * Get simple role label for display
 */
export function getRoleLabel(user: any): string {
  return getRoleDisplay(user).label;
}

/**
 * Check if user has admin privileges
 */
export function isAdminRole(user: any): boolean {
  const roleSlug = (user?.primaryRole?.slug || user?.role || '').toLowerCase();
  return roleSlug === 'super-admin' || roleSlug === 'admin' || roleSlug === 'administrator';
}

/**
 * Check if user has manager privileges
 */
export function isManagerRole(user: any): boolean {
  const roleSlug = (user?.primaryRole?.slug || user?.role || '').toLowerCase();
  return (
    roleSlug === 'manager' ||
    roleSlug === 'project-manager' ||
    isAdminRole(user)
  );
}
